import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ArrowLeft, Bot, Plus, Trash2, Loader2, Play, Pencil, Cloud, Cpu, HelpCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { PROVIDERS } from "@/lib/providers";
import { gegroepeerd } from "@/lib/aanbieder-locatie";
import { kortTijdstip } from "@/lib/vasttijdstip";
import MobileBottomNav, { BOTTOM_NAV_SPACER } from "../components/MobileBottomNav";
import { ShowYourWork, AgendaUitDeKennis } from "../components/MessageBubble";
import { splits as splitsAgendablok } from "../lib/agendablok";
import KindBadge from "../components/KindBadge";
import AgentTrailPanel from "../components/AgentTrailPanel";
import { useTaal } from "@/contexts/TaalContext";
import McpBevestigingen from "../components/McpBevestigingen";
import { wanneer as wanneerTijdstip, bronnenTekst, BRONNAMEN } from "../components/WorkerVoorstel";
import { BRONNEN, DAGEN_KORT, STANDAARD, zetDag, naarInvoer, vanInvoer, voorOpslaan } from "@/lib/workerplanning";
import { magKoppelen } from "../components/NextcloudConnection";


/** Which scope a base counts in, and what to call it on screen.
 *
 *  The listing gives `scope` and `project_id` but not the project's name, so
 *  the name is mapped here from the projects the person already has. Same
 *  arrangement as the knowledge screen; no second backend route that could
 *  answer differently.
 */
function scopeLabel(kb, projectsById) {
  const global = kb.scope ? kb.scope === "global" : !kb.project_id;
  if (global) return "Global";
  return projectsById[kb.project_id] || "Project";
}

/** Where this agent's words go, said before anyone presses Start.
 *
 *  The same shape of notice as the empty-domain-list message in Settings: the
 *  moment a choice means something leaves this machine, it is said in words on
 *  the screen where the choice is made, and not left to be discovered
 *  afterwards in the trail. Empty is not treated as a quiet local default,
 *  because it is not one: it follows the account, wherever that points.
 */
function leavesTheMachine(provider) {
  return Boolean(provider) && provider !== "ollama";
}

/** The tools an agent can be given, and what each of them reads.
 *
 *  Mirrors AGENT_TOOLS in services/agent_tools.py, and the backend filters
 *  what it is sent against that set, so a list that drifted here can only ever
 *  offer less than it should and never more. It grows by one name per GO.
 */
/** I6: de MCP-tools die op een kaart kunnen, als `mcp:<server-id>:<tool>`. Alleen
 *  van servers die gekoppeld, aan en getoetst zijn; de backend toetst hetzelfde
 *  bij opslaan en bij elke rit (`mcp_rechten`). PUUR. */
export function mcpKeuzes(servers) {
  return (servers || [])
    .filter(s => s.actief && s.aan && s.getoetst)
    // W1e: wat voor deze koppeling uit staat, komt niet op de kaart; de backend
    // laat het ook bij opslaan en bij elke rit vallen (`mcp_rechten.toegestaan`).
    .flatMap(s => (s.gereedschappen || []).filter(g => !(s.gereedschap_uit || []).includes(g.naam)).map(g => ({
      name: `mcp:${s.id}:${g.naam}`, tool: g.naam, titel: g.titel || "",
      beschrijving: g.beschrijving || "", server: s.naam,
    })));
}

const AGENT_TOOLS = [
  {
    name: "list_documents",
    label: "Documenten opsommen",
    reads: "de namen van de bestanden in de kennisbanken op deze kaart",
  },
  {
    name: "search_knowledge",
    label: "Kennis doorzoeken",
    reads: "hooguit drie passages uit diezelfde kennisbanken, per zoekopdracht",
  },
  {
    // W8b, besluit Frank 22 oktober 2026 (keuze a): alleen een concept, en alleen op
    // een mail-id dat in de opdracht staat. Versturen doe je zelf, vanuit het spoor.
    name: "draft_mail_reply",
    label: "Mailconcepten opstellen",
    reads: "alleen het Gmail-bericht waarvan het id in de opdracht staat, en verstuurt niets",
  },
];

/** How many rounds an agent gets, and what the ends of the dial mean.
 *
 *  The ceiling is the backend's agent_loop.MAX_ROUNDS. It is written once here
 *  and the backend pulls any number into the same range, so a screen that
 *  drifted could never let a value through that the loop would not honour.
 */
const MIN_ROUNDS = 1;
const MAX_ROUNDS = 8;
const DEFAULT_ROUNDS = 4;

/** Where the work of this card actually goes, in one sentence.
 *
 *  Frank's design rule from the glass test of 30 July: tools and model choice
 *  belong in one view. Giving an agent a tool that reads a knowledge base and
 *  a cloud model to run on are two ticks in two places, and separately they
 *  each look harmless. Together they mean the contents of a vault leave the
 *  machine, and that has to be said where the second tick happens.
 */
function runsOn(cardProvider, account) {
  if (cardProvider) {
    return {
      name: PROVIDERS[cardProvider]?.name || cardProvider,
      away: leavesTheMachine(cardProvider),
      fromCard: true,
    };
  }
  const provider = account?.provider || "";
  if (!provider) return { name: "whatever this account uses", away: false, unknown: true };
  return {
    name: PROVIDERS[provider]?.name || provider,
    away: leavesTheMachine(provider),
    fromCard: false,
  };
}

const EMPTY_FORM = {
  name: "", brief: "", standing_task: "", skill_slugs: [], knowledge_base_ids: [],
  provider: "", model: "", round_budget: DEFAULT_ROUNDS, tool_names: [],
  gedeeld: false,
  // W8a (Frank 22 okt 2026): web per agent, standaard uit.
  web_toegang: false,
  // DW-e (24 okt 2026): de dagbronnen en het vaste tijdstip van een worker.
  bronnen: [],
  tijdstip: null,
};


/** "maandag 26 oktober om 08:00" in de taal van het scherm. PUUR. GL200-1. */
export function volgendeKeer(iso, taal, zone) {
  if (!iso) return "";
  const d = new Date(iso);
  const en = taal === "en";
  // IN DE ZONE VAN DE WORKER en niet die van dit scherm: zijn uur geldt daar (huisregel 4.11).
  const tz = zone ? { timeZone: zone } : {};
  const dag = d.toLocaleDateString(en ? "en-GB" : "nl-NL", { weekday: "long", day: "numeric", month: "long", ...tz });
  const uur = d.toLocaleTimeString(en ? "en-GB" : "nl-NL", { hour: "2-digit", minute: "2-digit", ...tz });
  return `${dag} ${en ? "at" : "om"} ${uur}`;
}

export default function AgentsPage() {
  const { t, taal } = useTaal();
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [skills, setSkills] = useState([]);
  const [mcpServers, setMcpServers] = useState([]);
  const [rol, setRol] = useState("");
  const [bases, setBases] = useState([]);
  const [projects, setProjects] = useState([]);
  const [account, setAccount] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [task, setTask] = useState("");
  const [running, setRunning] = useState(false);
  // Vondst 11: de webschakelaar op het hoofdscherm, terwijl hij opslaat.
  const [webBezig, setWebBezig] = useState(false);
  const [result, setResult] = useState(null);

  // Rit C3, het antwoordkanaal. `waiting` komt van de server en overleeft
  // daarom een herlaadbeurt; `result` niet. Dat verschil IS het punt: tot
  // vandaag was een run die stopte met een vraag na een herlaadbeurt
  // onvindbaar, en op Franks database staan er vier die nooit zijn beantwoord.
  const [waiting, setWaiting] = useState([]);
  const [answer, setAnswer] = useState("");
  const [answering, setAnswering] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/agents`).then(r => {
        setAgents(r.data);
        // GL214-6 (glasronde 0.9.214): "Open bij Agenten" op de werkerkaart opent de agent zelf.
        const gevraagd = new URLSearchParams(window.location.search).get("agent");
        const agent = gevraagd && (Array.isArray(r.data) ? r.data : []).find(a => a.id === gevraagd);
        if (agent) setSelected(agent);
      }).catch(() => {}),
      axios.get(`${API}/skills`).then(r => setSkills(r.data)).catch(() => {}),
      // I6: MCP-tools voor de kaart, en je rol (een kijker krijgt ze niet).
      axios.get(`${API}/mcp-servers`).then(r => setMcpServers(Array.isArray(r.data) ? r.data : [])).catch(() => {}),
      axios.get(`${API}/organisatie`).then(r => setRol(r.data?.mijn_rol || "")).catch(() => {}),
      axios.get(`${API}/knowledge/bases`).then(r => setBases(r.data)).catch(() => {}),
      axios.get(`${API}/projects`).then(r => setProjects(r.data)).catch(() => {}),
      // Only so the form can say where the work goes when the card is empty.
      // Without it, "follows your account" is true and useless: it does not
      // say what the account currently is.
      axios.get(`${API}/settings`).then(r => setAccount(r.data)).catch(() => {}),
      // De wachtende runs, bij het openen. Zonder deze aanroep is een run die
      // gisteren een vraag stelde vandaag nergens meer te zien.
      // `Array.isArray` en niet `|| []`: een route die iets anders teruggeeft
      // dan een lijst - een foutlichaam, een tussenpagina, een lege `{}` -
      // haalt anders het HELE agentscherm onderuit bij de eerste klik, want
      // dan bestaat `.find` niet. Dat is dezelfde vorm als de ene `.slice` die
      // het kennisbankenscherm neerhaalde, en zij is hier gevonden doordat een
      // bestaande toets van een ander onderwerp omviel.
      axios.get(`${API}/agents/runs/waiting`)
        .then(r => setWaiting(Array.isArray(r.data) ? r.data : []))
        .catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const draait = runsOn(form.provider, account);
  const projectsById = Object.fromEntries((projects || []).map(p => [p.id, p.name]));
  const skillsBySlug = Object.fromEntries((skills || []).map(s => [s.slug, s]));
  const basesById = Object.fromEntries((bases || []).map(b => [b.id, b]));

  /** De uitslag die hoort bij een run die nog op een antwoord wacht.
   *
   *  EEN functie, want er zijn TWEE wegen naar dit vak - de balk bovenaan en
   *  de agent uit de linkerlijst - en twee plekken die dit los samenstellen
   *  zouden uit elkaar lopen op precies het veld dat het vak laat verschijnen.
   *  Dat is wat er op 26 augustus misging: er was er maar EEN.
   */
  const uitslagVanWachtende = (w) => ({
    conversation_id: w.conversation_id,
    // Geen `message_id`: die is hier niet bekend, en Show your work krijgt hem
    // dus ook niet. Een lege id doorgeven zou een blik opleveren die naar
    // niets kijkt.
    answer: w.question,
    steps: [],
    outcome: "asked",
    question: w.question,
  });

  const wachtendVoor = (agentId) =>
    (waiting || []).find(w => w.agent_id === agentId) || null;

  const selectAgent = (agent) => {
    setSelected(agent);
    setTask("");
    setAnswer("");
    // WACHT ER EEN RUN OP DEZE AGENT, DAN STAAT DIE METEEN OPEN.
    //
    // Franks glastest van 26 augustus. Na een herlaadbeurt stond de balk er
    // wel en het antwoordvak niet, want `result` was de enige bron voor dat
    // vak en deze functie zette hem op null. Wie de agent uit de lijst koos -
    // de gewoonste handeling die er is - wiste daarmee precies de toestand die
    // hij zocht.
    //
    // Er wordt hier NIETS gedraaid. De vraag ligt er al; wat ontbrak was een
    // plek om te antwoorden.
    const wacht = wachtendVoor(agent.id);
    setResult(wacht ? uitslagVanWachtende(wacht) : null);
  };

  /** De balk bijwerken uit wat een run zojuist teruggaf. Rit C3.
   *
   *  Uit de UITSLAG en niet met een tweede aanroep naar de server. Het scherm
   *  weet hier wat er is gebeurd; het opnieuw gaan vragen zou een venster
   *  openen waarin de balk iets anders zegt dan het paneel eronder.
   *
   *  `gesprek` is het gesprek waar de handeling NAARTOE ging, niet het gesprek
   *  in het antwoord. Bij een vervolg zijn die gelijk, en juist daarom is het
   *  goedkoop om hier de bron te nemen die ook klopt als dat ooit verandert.
   */
  const boekWachtend = (gesprek, uitslag, agent) => {
    const vraag = uitslag?.outcome === "asked" ? (uitslag.question || "") : "";
    setWaiting(vorige => {
      const zonder = (vorige || []).filter(w => w.conversation_id !== gesprek);
      if (!vraag) return zonder;
      return [{
        conversation_id: gesprek,
        agent_id: agent?.id || "",
        agent_name: agent?.name || "",
        question: vraag,
        task: "",
        asked_at: "",
      }, ...zonder];
    });
  };

  /** Een wachtende run openen vanuit de balk.
   *
   *  Er wordt hier NIETS gedraaid. De vraag ligt er al; wat ontbreekt is een
   *  plek om te antwoorden. Een klik die stiekem een model aanroept zou een
   *  beurt kosten die niemand heeft besteld.
   */
  const openWaiting = (w) => {
    const agent = (agents || []).find(a => a.id === w.agent_id);
    if (agent) setSelected(agent);
    setTask("");
    setAnswer("");
    setResult(uitslagVanWachtende(w));
  };

  const sendAnswer = async () => {
    const tekst = (answer || "").trim();
    // Leeg wordt niet verstuurd. De server weigert het ook, maar een aanroep
    // die je van tevoren weet af te wijzen hoort niet de deur uit.
    if (!tekst || !result?.conversation_id || answering) return;
    const gesprek = result.conversation_id;
    setAnswering(true);
    try {
      const res = await axios.post(`${API}/agents/runs/${gesprek}/answer`, { answer: tekst });
      setResult(res.data);
      setAnswer("");
      boekWachtend(gesprek, res.data, selected);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Het antwoord kwam niet aan bij de run"));
    } finally {
      setAnswering(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  // GLASRONDE 0.9.189 VONDST 11 (Frank 22 oktober 2026): Mag op het web ook op het
  // hoofdscherm, naast Bewerken. Hij stuurt ALLEEN `web_toegang`; de route zet een veld
  // alleen als het is meegestuurd, dus de rest van de kaart blijft wat hij was.
  const zetWeb = async (aan) => {
    const id = selected?.id;
    if (!id) return;
    setWebBezig(true);
    try {
      const res = await axios.put(`${API}/agents/${id}`, { web_toegang: aan });
      setAgents(prev => prev.map(a => (a.id === id ? res.data : a)));
      setSelected(prev => (prev?.id === id ? res.data : prev));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Opslaan mislukt"));
    } finally {
      setWebBezig(false);
    }
  };

  const openEdit = (agent) => {
    setEditingId(agent.id);
    setForm({
      name: agent.name,
      brief: agent.brief || "",
      standing_task: agent.standing_task || "",
      skill_slugs: [...(agent.skill_slugs || [])],
      knowledge_base_ids: [...(agent.knowledge_base_ids || [])],
      provider: agent.provider || "",
      model: agent.model || "",
      round_budget: agent.round_budget || DEFAULT_ROUNDS,
      tool_names: [...(agent.tool_names || [])],
      gedeeld: !!agent.gedeeld,
      web_toegang: !!agent.web_toegang,
      bronnen: [...(agent.bronnen || [])],
      tijdstip: agent.tijdstip ? { ...agent.tijdstip, dagen: [...(agent.tijdstip.dagen || [])] } : null,
    });
    setFormOpen(true);
  };

  const toggleIn = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value],
    }));
  };

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    // A number input hands back a string, and an emptied one hands back "".
    // Neither is a round budget, and sending either would be a 422 about a
    // field the person was not thinking about. The backend pulls the number
    // into range as well; this is so the request is a request at all.
    const { bronnen, tijdstip, ...rest } = form;
    const body = {
      ...rest,
      round_budget: Number(form.round_budget) || DEFAULT_ROUNDS,
      // DW-e: bronnen en tijdstip gaan alleen mee bij het bewerken (de aanmaakroute kent ze niet).
      ...(editingId ? { bronnen, tijdstip: voorOpslaan(tijdstip) } : {}),
    };
    try {
      if (editingId) {
        const res = await axios.put(`${API}/agents/${editingId}`, body);
        setAgents(prev => prev.map(a => (a.id === editingId ? res.data : a)));
        setSelected(prev => (prev?.id === editingId ? res.data : prev));
        toast.success(t("Agent opgeslagen"));
      } else {
        const res = await axios.post(`${API}/agents`, body);
        setAgents(prev => [...prev, res.data]);
        setSelected(res.data);
        toast.success(t("Agent aangemaakt"));
      }
      setFormOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Deze agent kon niet worden opgeslagen"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (agentId) => {
    try {
      await axios.delete(`${API}/agents/${agentId}`);
      setAgents(prev => prev.filter(a => a.id !== agentId));
      if (selected?.id === agentId) { setSelected(null); setResult(null); }
      toast.success(t("Agent verwijderd"));
    } catch {
      toast.error(t("Deze agent kon niet worden verwijderd"));
    }
  };

  const start = async () => {
    if (!selected) return;
    setRunning(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/agents/${selected.id}/run`, { task });
      setResult(res.data);
      setAnswer("");
      // Een verse run die stopte met een vraag hoort meteen in de balk, zodat
      // hij er nog staat als dit scherm wordt verlaten of herladen.
      boekWachtend(res.data.conversation_id, res.data, selected);
    } catch (e) {
      // The 400 from a bare agent with nothing to do arrives here as the
      // sentence the backend wrote. Shown as it is, because it names the two
      // ways out and a generic failure names none.
      toast.error(e.response?.data?.detail || t("De run is niet afgemaakt"));
    } finally {
      setRunning(false);
    }
  };

  /** One row in the list. Shared, so the list cannot drift from itself.
   *
   *  Everything here survives an empty field. The knowledge screen learned
   *  that the hard way: one `.slice` on a value that a new kind of row leaves
   *  empty took the whole list down, and it could not be seen until such a row
   *  existed. An agent with no brief and no skills is the ordinary first
   *  thing somebody makes.
   */
  const renderAgent = (agent) => {
    const skillCount = (agent.skill_slugs || []).length;
    const baseCount = (agent.knowledge_base_ids || []).length;
    // Resolved once and used both to decide and to print. Deciding on the
    // fallback and printing the raw value is how "up to undefined rounds"
    // reaches a screen, and a row from before this column existed has exactly
    // that shape.
    const rondes = agent.round_budget || DEFAULT_ROUNDS;
    const toolCount = (agent.tool_names || []).length;
    return (
      <div
        key={agent.id}
        data-testid={`agent-row-${agent.id}`}
        className={`p-3 rounded-lg cursor-pointer transition-colors ${selected?.id === agent.id ? 'bg-accent/10 border border-accent/30' : 'hover:bg-muted/50 border border-transparent'}`}
        onClick={() => selectAgent(agent)}
      >
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-medium text-sm truncate">{agent.name}</h3>
          <Button
            variant="ghost" size="icon"
            aria-label={`Delete ${agent.name}`}
            data-testid={`agent-delete-${agent.id}`}
            className="tap-target h-8 w-8 md:h-6 md:w-6 opacity-100 md:opacity-0 md:hover:opacity-100 shrink-0"
            onClick={e => { e.stopPropagation(); remove(agent.id); }}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground truncate">
          {agent.brief || t("Nog geen opdracht")}
        </p>
        {/* GL214-7 (glasronde 0.9.214): een vast tijdstip kort in de lijst, "ma 09:00". */}
        {kortTijdstip(agent.tijdstip, taal) && (
          <p className="text-xs text-muted-foreground" data-testid={`agent-tijdstip-${agent.id}`}>
            {kortTijdstip(agent.tijdstip, taal)}
          </p>
        )}
        <p className="text-xs text-muted-foreground mt-1">
          {[
            skillCount === 1 ? t("1 skill") : t("{n} skills", { n: skillCount }),
            baseCount === 1 ? t("1 kennisbank") : t("{n} kennisbanken", { n: baseCount }),
            ...(rondes > 1 ? [t("hooguit {n} rondes", { n: rondes })] : []),
            ...(toolCount > 0 ? [toolCount === 1 ? t("1 gereedschap") : t("{n} gereedschappen", { n: toolCount })] : []),
            // GL200-2: de dagbronnen die hij leest, bij naam.
            ...((agent.bronnen || []).length > 0 ? [t("leest {bronnen}", { bronnen: bronnenTekst(agent.bronnen) })] : []),
          ].join(", ")}
        </p>
      </div>
    );
  };

  return (
    <div className={`h-screen flex flex-col bg-background ${BOTTOM_NAV_SPACER}`}>
      <header className="flex items-center px-4 gap-3 border-b min-h-12 pt-[var(--veilig-boven)]">
        <Button variant="ghost" size="icon" onClick={() => navigate("/")} className="tap-target h-8 w-8">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="font-serif text-lg font-light">
          <Bot className="h-4 w-4 inline mr-2 text-[hsl(var(--accent))]" />
          {t("Agenten")}
        </h1>
        <Button variant="outline" size="sm" className="ml-auto" data-testid="new-agent-btn" onClick={openCreate}>
          <Plus className="h-3 w-3 mr-1" />{t("Nieuwe agent")}
        </Button>
      </header>

      {/* I4: MCP-aanroepen die een agent voorstelde, bovenaan om dezelfde reden
          als de wachtende runs: dit vraagt om JOU. Opnieuw opgehaald na elke run. */}
      <McpBevestigingen ververs={result} />

      {/* DE WACHTENDE RUNS, BOVENAAN EN NIET IN EEN HOEK. Rit C3.

          Hij staat hier omdat een run die op jou wacht het enige op dit scherm
          is dat om JOU vraagt. Alles eronder is werk dat je zelf start.

          Franks eis van 26 augustus staat erin verwerkt: hij verloor drie
          rondes aan een naamloos icoon van veertien pixels, dus hier staat wat
          er aan de hand is IN WOORDEN, met de vraag zelf erbij en niet alleen
          een telling. Een balk die zegt "1 wachtend" dwingt een klik om te
          weten of het de moeite waard is.

          En hij verdwijnt als er niets wacht. Een lege balk die er altijd
          staat, leest binnen een week als meubilair. */}
      {(waiting || []).length > 0 && (
        <div className="border-b bg-[hsl(var(--accent))]/5 px-4 py-2 space-y-1" data-testid="waiting-runs">
          <p className="text-xs font-medium text-[hsl(var(--accent))]">
            <HelpCircle className="h-3 w-3 inline mr-1" />
            {waiting.length === 1
              ? t("Een run is gestopt en wacht op je antwoord")
              : `${waiting.length} runs stopped and are waiting for your answer`}
          </p>
          {waiting.map(w => (
            <button
              key={w.conversation_id}
              type="button"
              onClick={() => openWaiting(w)}
              data-testid={`waiting-run-${w.conversation_id}`}
              className="block w-full text-left text-xs rounded px-2 py-1 hover:bg-[hsl(var(--accent))]/10 tap-target"
            >
              <span className="font-medium">{w.agent_name}</span>
              <span className="text-muted-foreground"> {t("vroeg:")} </span>
              <span>{w.question}</span>
              {/* DAT HET EEN KNOP IS, STAAT ER IN WOORDEN. Franks glastest van
                  26 augustus: hij vond de balk en zocht daarna naar een plek
                  om te antwoorden. Een regel die eruitziet als tekst, wordt
                  gelezen als tekst, hoe klikbaar zij ook is. */}
              <span className="text-[hsl(var(--accent))] underline whitespace-nowrap"> {t("Beantwoord het")}</span>
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-col md:flex-row flex-1 min-h-0">
        {/* DEZELFDE KNIJP ALS OP HET KENNISBANKENSCHERM, en hij is hier gevonden
            doordat de ijking van die reparatie de verboden vorm nog ergens
            anders aantrof (23 augustus 2026). `max-h-40` is 160 pixels en gold
            ALLEEN onder `md`, dus alleen op een telefoon; met een kop erin past
            er ongeveer een regel. Frank zag het op het kennisbankenscherm; hier
            stond precies hetzelfde te wachten. */}
        <aside className="w-full md:w-72 border-b md:border-b-0 md:border-r overflow-y-auto p-3 space-y-2 max-h-[60vh] md:max-h-none shrink-0">
          {loading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : agents.length === 0 ? (
            <p className="text-xs text-muted-foreground px-1" data-testid="no-agents">
              {t("Nog geen agenten. Een agent draagt zijn eigen opdracht, skills en kennis, zodat een run volgende week hetzelfde leest.")}
            </p>
          ) : agents.map(renderAgent)}
        </aside>

        <main className="flex-1 flex flex-col min-w-0 p-4 overflow-y-auto">
          {!selected ? (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              <div className="text-center"><Bot className="h-12 w-12 mx-auto mb-3 opacity-30" /><p>{t("Kies een agent")}</p></div>
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="min-w-0">
                  <h2 className="text-xl font-serif truncate">{selected.name}</h2>
                  <p className="text-sm text-muted-foreground">{selected.brief || t("Nog geen opdracht")}</p>
                </div>
                {/* Off while it runs. Editing the card underneath a run in
                    progress would change what the trail is a record of. */}
                <div className="flex items-center gap-3 shrink-0">
                  <label className="flex items-center gap-2" data-testid="agent-web-hoofd">
                    <Switch
                      checked={Boolean(selected.web_toegang)}
                      disabled={running || webBezig}
                      onCheckedChange={zetWeb}
                      aria-label={t("Mag op het web")}
                      data-testid="agent-web-hoofd-schakelaar"
                    />
                    <span data-testid="agent-web-label"
                          className={cn("text-xs", selected.web_toegang ? "font-medium" : "text-muted-foreground")}>
                      {selected.web_toegang ? t("web aan") : t("web uit")}
                    </span>
                  </label>
                  <Button
                    variant="outline" size="sm" data-testid="edit-agent-btn"
                    disabled={running} onClick={() => openEdit(selected)}
                  >
                    <Pencil className="h-3 w-3 mr-1" />{t("Bewerken")}
                  </Button>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 mb-4">
                <section data-testid="agent-skills">
                  <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">{t("Skills")}</h3>
                  {(selected.skill_slugs || []).length === 0 ? (
                    <p className="text-xs text-muted-foreground">{t("Geen. Hij werkt alleen vanuit zijn opdracht.")}</p>
                  ) : (
                    <ul className="text-sm space-y-0.5">
                      {selected.skill_slugs.map(slug => (
                        <li key={slug} className="truncate">
                          {skillsBySlug[slug]?.name || <span className="font-mono text-xs">{slug}</span>}
                          {!skillsBySlug[slug] && <span className="text-xs text-muted-foreground"> {t("(niet geïnstalleerd)")}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                <section data-testid="agent-knowledge">
                  <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">{t("Kennis")}</h3>
                  {(selected.knowledge_base_ids || []).length === 0 ? (
                    <p className="text-xs text-muted-foreground">{t("Geen. Hij werkt met wat jij hem geeft.")}</p>
                  ) : (
                    <ul className="text-sm space-y-0.5">
                      {selected.knowledge_base_ids.map(id => (
                        <li key={id} className="truncate">
                          {basesById[id] ? (
                            <>
                              {basesById[id].name}
                              <span className="text-xs text-muted-foreground"> ({scopeLabel(basesById[id], projectsById)})</span>
                              {" "}<KindBadge kb={basesById[id]} className="inline-block align-middle" />
                            </>
                          ) : (
                            // Named on the card and not in your list. The trail
                            // is where the run says what it actually worked
                            // from; this only says it is not yours to see.
                            <span className="text-xs text-muted-foreground">{t("Een bank is niet meer voor je beschikbaar")}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </div>

              {/* Wat deze agent heeft gedaan, over al zijn gesprekken heen.
                  Een run IS een gesprek, dus dit spoor bestond al; het was
                  alleen alleen per gesprek te vinden en dus niet per agent.
                  De ketenstaat per regel komt van de server, tegen de hele
                  keten van het gesprek waar de regel in staat. */}
              <div className="mb-4">
                <AgentTrailPanel agentId={selected.id} key={`${selected.id}:${result?.message_id || ""}`} />
              </div>

              <div className="flex gap-3 mb-2">
                <Input
                  value={task}
                  onChange={e => setTask(e.target.value)}
                  placeholder={t("Wat moet hij doen?")}
                  aria-label={t("Taak")}
                  data-testid="agent-task-input"
                  className="flex-1"
                  onKeyDown={e => e.key === 'Enter' && !running && start()}
                />
                <Button onClick={start} disabled={running} data-testid="start-agent-btn">
                  {running ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Play className="h-3 w-3 mr-1" />}{t("Start")}
                </Button>
              </div>
              {/* The same fact again, where the finger is. The form says it
                  while you choose; this says it while you start. */}
              {leavesTheMachine(selected.provider) && (
                <p className="text-xs text-[hsl(var(--accent))] mb-1" data-testid="run-leaves-machine">
                  <Cloud className="h-3 w-3 inline mr-1" />
                  {t("Verlaat de machine: deze run gaat naar {waar}.", {
                    waar: (PROVIDERS[selected.provider]?.name || selected.provider) + (selected.model ? ` (${selected.model})` : "") })}
                </p>
              )}
              <p className="text-xs text-muted-foreground mb-4">
                {selected.standing_task
                  ? t("Laat je het leeg, dan doet hij zijn vaste taak: {taak}", { taak: selected.standing_task })
                  : t("Deze agent heeft geen vaste taak, dus geef hem iets te doen.")}
              </p>
              {/* GL200-2 (glasronde 0.9.200): welke bronnen hij mag lezen. TASQ stond nergens,
                  terwijl hij het wel las; het staat op de kaart als dagbron, niet als gereedschap. */}
              {(selected.bronnen || []).length > 0 && (
                <p className="text-xs mb-2" data-testid="agent-bronnen">
                  {t("Mag lezen: {bronnen}. Alleen lezen; schrijven doet hij niet.", { bronnen: bronnenTekst(selected.bronnen) })}
                </p>
              )}
              {/* GL200-1 (glasronde 0.9.200): het tijdstip van een worker en de volgende rit. */}
              {selected.tijdstip && (
                <p className="text-xs mb-4" data-testid="agent-planning">
                  {t("Planning: {wanneer}.", { wanneer: wanneerTijdstip(selected.tijdstip, taal) })}
                  {selected.volgende_rit
                    ? " " + t("Volgende keer: {moment}, alleen als de Mac aan staat.", { moment: volgendeKeer(selected.volgende_rit, taal, selected.tijdstip.tijdzone) })
                    : ""}
                </p>
              )}

              {/* What is happening while it happens. The rounds themselves
                  arrive in Show your work when the run returns; this says how
                  many there could be, so a run that takes a while does not
                  read as a hang. */}
              {running && (
                <div className="border-t pt-4 text-sm text-muted-foreground" data-testid="agent-running">
                  <Loader2 className="h-3 w-3 animate-spin inline mr-2" />
                  {(selected.round_budget || DEFAULT_ROUNDS) === 1
                    ? t("Bezig. Hooguit 1 ronde, een aanroep van het model. Elke ronde verschijnt onder Laat je werk zien zodra hij klaar is.")
                    : t("Bezig. Hooguit {n} rondes, elk een aanroep van het model. Elke ronde verschijnt onder Laat je werk zien zodra hij klaar is.", { n: selected.round_budget || DEFAULT_ROUNDS })}
                </div>
              )}

              {result && (
                <div className="border-t pt-4" data-testid="agent-result">
                  {/* Glasronde 0.9.189 vondst 9 (Frank 22 oktober 2026): het bronblok stond hier
                      als ruwe tekst ("--- [uit de bronnen] ... bron :: ..."). Nu dezelfde knip en
                      hetzelfde blok als onder een antwoord in de chat. */}
                  <p className="text-sm whitespace-pre-wrap" data-testid="agent-result-tekst">{splitsAgendablok(result.answer).tekst}</p>
                  {splitsAgendablok(result.answer).agenda && <AgendaUitDeKennis agenda={splitsAgendablok(result.answer).agenda} />}
                  {/* Alleen wanneer er een bericht is om naar te kijken. Een
                      run die vanuit de balk is geopend heeft hier geen
                      bericht-id, en een lege id doorgeven zou een blik
                      opleveren die naar niets kijkt. */}
                  {result.message_id && (
                    <ShowYourWork conversationId={result.conversation_id} messageId={result.message_id} />
                  )}
                  {result.duration_ms ? (
                    <p className="text-xs text-muted-foreground font-mono mt-2">
                      {result.final_provider} / {result.final_model} - {result.duration_ms}ms
                    </p>
                  ) : null}

                  {/* HET ANTWOORDKANAAL. Rit C3.

                      DE TOONVOORWAARDE IS `outcome`, EN NOOIT DE TEKST VAN HET
                      ANTWOORD. Zou dit vak verschijnen zodra er ergens "stopped
                      with a question" in staat, dan hangt een knop aan een zin,
                      en die zin is een vertaalbare melding.

                      Het antwoord gaat naar DEZELFDE run: de agent gaat verder
                      met zijn brief, zijn kaart en zijn geschiedenis. Dat is
                      iets anders dan het antwoord in een nieuwe opdracht
                      plakken, en dat verschil is de hele rit. */}
                  {result.outcome === "asked" && (
                    <div className="mt-4 border-t pt-3" data-testid="agent-asked">
                      <p className="text-xs font-medium text-[hsl(var(--accent))] mb-1">
                        <HelpCircle className="h-3 w-3 inline mr-1" />
                        {t("Hij stopte en vroeg je iets")}
                      </p>
                      <p className="text-sm mb-2 whitespace-pre-wrap">{result.question}</p>
                      <div className="flex gap-2">
                        <Input
                          value={answer}
                          onChange={e => setAnswer(e.target.value)}
                          placeholder={t("Je antwoord")}
                          aria-label={t("Antwoord op de vraag van de agent")}
                          data-testid="agent-answer-input"
                          className="flex-1"
                          onKeyDown={e => e.key === "Enter" && !answering && sendAnswer()}
                        />
                        {/* Met tekst en niet alleen een icoon. Franks eis van
                            26 augustus, na drie rondes zoeken naar een naamloos
                            icoon van veertien pixels. */}
                        <Button onClick={sendAnswer} disabled={answering} data-testid="agent-answer-btn">
                          {answering
                            ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                            : <Send className="h-3 w-3 mr-1" />}
                          {t("Antwoord sturen")}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t("Hij gaat verder in dezelfde run, met alles wat hij al had.")}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {/* Create and edit are one form. Two would drift, and the fields are the
          same question either way: what is it for, and what does it carry. */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? t("Agent bewerken") : t("Nieuwe agent")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="agent-name">{t("Naam")}</Label>
              <Input id="agent-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder={t("bijv. Verslaggever")} />
            </div>
            <div>
              <Label htmlFor="agent-brief">{t("Opdracht")}</Label>
              <Textarea
                id="agent-brief" value={form.brief}
                onChange={e => setForm({ ...form, brief: e.target.value })}
                placeholder={t("Waar deze agent voor is. Hij vervangt je persona zolang hij draait.")}
                className="min-h-[70px] text-sm"
              />
            </div>
            <div>
              <Label htmlFor="agent-standing">{t("Vaste taak")}</Label>
              <Input
                id="agent-standing" value={form.standing_task}
                onChange={e => setForm({ ...form, standing_task: e.target.value })}
                placeholder={t("Optioneel. Wat hij doet als je hem zonder iets start.")}
              />
            </div>

            {/* Which model answers. Empty is the ordinary case and it stays
                first in the list, because most agents should follow the
                account rather than pin themselves to one company. */}
            <div className="space-y-2">
              <Label htmlFor="agent-provider">{t("Model")}</Label>
              <select
                id="agent-provider"
                value={form.provider}
                onChange={e => setForm({ ...form, provider: e.target.value, model: "" })}
                data-testid="agent-provider-select"
                className="flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm"
              >
                <option value="">{t("Wat dit account gebruikt")}</option>
                {gegroepeerd(PROVIDERS).map((groep) => (
                  <optgroup key={groep.plek} label={t(groep.tekst)} data-testid={`agent-aanbieders-${groep.plek}`}>
                    {groep.items.map(([key, prov]) => (
                      <option key={key} value={key}>{prov.name}</option>
                    ))}
                  </optgroup>
                ))}
              </select>

              {(PROVIDERS[form.provider]?.models || []).length > 0 ? (
                <select
                  aria-label={t("Model")}
                  value={form.model}
                  onChange={e => setForm({ ...form, model: e.target.value })}
                  data-testid="agent-model-select"
                  className="flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm"
                >
                  <option value="">{t("Standaard voor deze aanbieder")}</option>
                  {PROVIDERS[form.provider].models.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              ) : (
                // No catalog to pick from: either no provider is chosen yet, or
                // it is Ollama, whose tags live on this machine and differ per
                // machine. Typed, then, rather than guessed at from a list that
                // could name something this computer does not have.
                <Input
                  aria-label={t("Model")}
                  value={form.model}
                  onChange={e => setForm({ ...form, model: e.target.value })}
                  data-testid="agent-model-input"
                  placeholder={t("Optioneel. Leeg betekent het standaardmodel.")}
                />
              )}

              {/* Said here, at the field, and before the run. Discovering in
                  the trail afterwards that something left the machine is one
                  message too late. */}
              {leavesTheMachine(form.provider) ? (
                <p className="text-xs text-[hsl(var(--accent))]" data-testid="agent-leaves-machine">
                  <Cloud className="h-3 w-3 inline mr-1" />
                  {t("Deze agent verlaat de machine. Elke run gaat naar {waar}, niet naar het model op dit apparaat, en het spoor zegt dat.", {
                    waar: PROVIDERS[form.provider]?.name || form.provider })}
                </p>
              ) : form.provider === "ollama" ? (
                <p className="text-xs text-muted-foreground" data-testid="agent-stays-local">
                  <Cpu className="h-3 w-3 inline mr-1" />
                  {t("Deze agent blijft op deze machine.")}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground" data-testid="agent-model-follows-account">
                  {t("Leeg betekent dat hij je accountinstelling volgt, waar die ook naar wijst, ook als je hem later wijzigt.")}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="agent-rounds">{t("Rondes")}</Label>
              <Input
                id="agent-rounds"
                type="number"
                min={MIN_ROUNDS}
                max={MAX_ROUNDS}
                value={form.round_budget}
                onChange={e => setForm({ ...form, round_budget: e.target.value })}
                data-testid="agent-rounds-input"
              />
              <p className="text-xs text-muted-foreground" data-testid="agent-rounds-note">
                {Number(form.round_budget) <= 1
                  ? t("Een ronde. Hij antwoordt een keer, en er wordt achteraf niets nagekeken.")
                  : t("Hooguit {n} rondes. Elke ronde is een aanroep van het model, en of hij zegt klaar te zijn wordt nagekeken voordat de rit stopt.", { n: Math.min(MAX_ROUNDS, Math.max(MIN_ROUNDS, Number(form.round_budget) || DEFAULT_ROUNDS)) })}
                {/* Glasronde 0.9.189 vondst 10 (Frank 22 oktober 2026): hier stond vast "Hij heeft
                    nog steeds geen gereedschap", ook met gereedschap of web aangevinkt. De zin
                    volgt nu wat er op de kaart staat. */}
                {Number(form.round_budget) > 1 && (
                  <>{" "}{form.tool_names.length > 0 || form.web_toegang
                    ? t("Hij gebruikt het gereedschap dat hieronder is aangevinkt.")
                    : t("Er is geen gereedschap aangevinkt.")}</>
                )}
              </p>
            </div>

            <div className="space-y-2">
              <Label>{t("Skills")}</Label>
              {skills.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  {t("Geen skills geïnstalleerd.")}{" "}
                  <button type="button" className="underline text-[hsl(var(--accent))]" onClick={() => navigate("/skills")}>{t("Haal er een paar op het scherm Skills")}</button>.
                </p>
              ) : (
                <div className="space-y-1 max-h-32 overflow-y-auto border rounded-md p-2">
                  {skills.map(s => (
                    <label key={s.slug} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={form.skill_slugs.includes(s.slug)}
                        onCheckedChange={() => toggleIn("skill_slugs", s.slug)}
                        aria-label={s.name}
                        data-testid={`skill-check-${s.slug}`}
                      />
                      <span className="truncate">{s.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Tools, and directly above them where the work goes. One view,
                because separately a tool that reads a vault and a cloud model
                each look harmless. */}
            <div className="space-y-2">
              <div className="flex items-baseline justify-between gap-2">
                <Label>{t("Gereedschap")}</Label>
                <span
                  className={`text-xs ${draait.away ? "text-[hsl(var(--accent))]" : "text-muted-foreground"}`}
                  data-testid="tools-runs-on"
                >
                  {t("Draait op")} {draait.name}{draait.fromCard ? "" : ` (${t("dit account")})`}
                  {draait.away ? `, ${t("en dat is buiten deze machine")}` : ""}
                </span>
              </div>
              <div className="space-y-1 border rounded-md p-2">
                {AGENT_TOOLS.map(tool => (
                  <label key={tool.name} className="flex items-start gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={form.tool_names.includes(tool.name)}
                      onCheckedChange={() => toggleIn("tool_names", tool.name)}
                      aria-label={t(tool.label)}
                      data-testid={`tool-check-${tool.name}`}
                    />
                    <span>
                      <span className="font-mono text-xs">{tool.name}</span>
                      <span className="text-xs text-muted-foreground"> {t("leest")} {t(tool.reads)}</span>
                    </span>
                  </label>
                ))}
              </div>
              {/* I6: MCP-tools van je eigen servers. Sinds W1h (23 okt) draait lezen en
                  zoeken direct en wacht een doe-gereedschap op je bevestiging (I4);
                  een kijker krijgt deze lijst niet. */}
              {magKoppelen(rol) && mcpKeuzes(mcpServers).length > 0 && (
                <div className="space-y-1 border rounded-md p-2" data-testid="mcp-tools">
                  <p className="text-xs text-muted-foreground">
                    {t("MCP-gereedschap. Lezen en zoeken gaat direct; bestellen, een link maken of iets aanvragen wacht eerst op je bevestiging.")}
                  </p>
                  {mcpKeuzes(mcpServers).map(tool => (
                    <label key={tool.name} className="flex items-start gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={form.tool_names.includes(tool.name)}
                        onCheckedChange={() => toggleIn("tool_names", tool.name)}
                        aria-label={tool.titel || tool.tool}
                        data-testid={`mcp-tool-check-${tool.tool}`}
                      />
                      <span>
                        <span className="font-mono text-xs">{tool.tool}</span>
                        <span className="text-xs text-muted-foreground"> {t("op")} {tool.server}</span>
                        {tool.beschrijving && (
                          <span className="block text-xs text-muted-foreground">{tool.beschrijving}</span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              )}
              {form.tool_names.length > 0 && draait.away && (
                <p className="text-xs text-[hsl(var(--accent))]" data-testid="tools-leave-warning">
                  <Cloud className="h-3 w-3 inline mr-1" />
                  {t("Wat deze gereedschappen lezen gaat met de vraag naar {waar}. Daar horen ook de bestandsnamen bij in de kennisbanken op deze kaart.", { waar: draait.name })}
                </p>
              )}
              {form.tool_names.length > 0 && !draait.away && (
                <p className="text-xs text-muted-foreground" data-testid="tools-stay-local">
                  <Cpu className="h-3 w-3 inline mr-1" />
                  {t("Wat dit gereedschap leest blijft op deze machine, omdat het model dat ook doet.")}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                {t("Verder is niets bereikbaar: geen netwerk, geen bestanden, geen geheugen. Gereedschap dat hier niet is aangevinkt wordt in de code geweigerd, ook als de opdracht erom vraagt.")}
              </p>
            </div>

            <div className="space-y-2">
              <Label>{t("Kennisbanken")}</Label>
              {bases.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  {t("Nog geen kennisbanken.")}{" "}
                  <button type="button" className="underline text-[hsl(var(--accent))]" onClick={() => navigate("/knowledge")}>{t("Maak er een op het scherm Knowledge")}</button>.
                </p>
              ) : (
                <div className="space-y-1 max-h-32 overflow-y-auto border rounded-md p-2">
                  {bases.map(kb => (
                    <label key={kb.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={form.knowledge_base_ids.includes(kb.id)}
                        onCheckedChange={() => toggleIn("knowledge_base_ids", kb.id)}
                        aria-label={kb.name}
                        data-testid={`base-check-${kb.id}`}
                      />
                      <span className="truncate">{kb.name}</span>
                      {/* Which scope it counts in, next to the name. Ticking a
                          base without knowing whether it is yours everywhere or
                          only inside one project is ticking a box blind. */}
                      <span className="text-xs text-muted-foreground shrink-0">{scopeLabel(kb, projectsById)}</span>
                      {/* And what it holds, which matters most here of the
                          three places it appears: ticking a reference base is
                          what switches this agent to a second turn. */}
                      <KindBadge kb={kb} />
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* F1-vervolg (16 oktober 2026): delen met de organisatie. De backend
                kent `gedeeld` sinds F1d en weigert een kijker zelf; dit is alleen
                de schakelaar die er nog niet was. */}
            <div className="flex items-start justify-between gap-3 border-t pt-3">
              <div>
                <Label htmlFor="agent-gedeeld">{t("Delen met organisatie")}</Label>
                <p className="text-xs text-muted-foreground">
                  {t("Iedereen in de organisatie kan deze agent dan zien en starten. Kennisbanken en skills komen van wie hem start.")}
                </p>
              </div>
              <Switch
                id="agent-gedeeld"
                checked={form.gedeeld}
                onCheckedChange={(aan) => setForm(prev => ({ ...prev, gedeeld: aan }))}
                data-testid="agent-gedeeld"
              />
            </div>

            {/* DW-e (besluit Frank 24 okt 2026): welke dagbronnen hij leest en wanneer hij draait. */}
            {editingId && (
              <div className="space-y-2 border-t pt-3" data-testid="agent-bronnen-bewerken">
                <Label>{t("Mag lezen")}</Label>
                <div className="flex flex-wrap gap-2">
                  {BRONNEN.map((b) => (
                    <label key={b} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={form.bronnen.includes(b)}
                        onCheckedChange={() => toggleIn("bronnen", b)}
                        aria-label={BRONNAMEN[b]}
                        data-testid={`bron-check-${b}`}
                      />
                      <span>{BRONNAMEN[b]}</span>
                    </label>
                  ))}
                </div>
                <div className="flex items-start justify-between gap-3 pt-2">
                  <div>
                    <Label htmlFor="agent-tijdstip">{t("Vast tijdstip")}</Label>
                    <p className="text-xs text-muted-foreground">
                      {t("Hij draait dan vanzelf op deze dagen en dit uur, alleen als de Mac aan staat.")}
                    </p>
                  </div>
                  <Switch
                    id="agent-tijdstip"
                    checked={Boolean(form.tijdstip)}
                    onCheckedChange={(aan) => setForm(prev => ({ ...prev, tijdstip: aan ? (prev.tijdstip || { ...STANDAARD }) : null }))}
                    data-testid="agent-tijdstip-aan"
                  />
                </div>
                {form.tijdstip && (
                  <div className="flex flex-wrap items-center gap-2" data-testid="agent-tijdstip">
                    {DAGEN_KORT[taal === "en" ? "en" : "nl"].map((naam, d) => (
                      <Button key={naam} type="button" size="sm"
                        variant={form.tijdstip.dagen.includes(d) ? "default" : "outline"}
                        aria-pressed={form.tijdstip.dagen.includes(d)}
                        onClick={() => setForm(prev => ({ ...prev, tijdstip: zetDag(prev.tijdstip, d) }))}
                        data-testid={`dag-${d}`}>
                        {naam}
                      </Button>
                    ))}
                    <Input type="time" className="w-28" aria-label={t("Tijd")}
                      value={naarInvoer(form.tijdstip)}
                      onChange={(e) => { const tekst = e.target.value; setForm(prev => ({ ...prev, tijdstip: vanInvoer(prev.tijdstip, tekst) })); }}
                      data-testid="agent-tijd" />
                  </div>
                )}
              </div>
            )}

            {/* W8a, BESLUIT FRANK 22 OKTOBER 2026: op het web, per agent, standaard
                uit. Alleen pagina's lezen; elke pagina staat in zijn spoor. */}
            <div className="flex items-start justify-between gap-3 border-t pt-3">
              <div>
                <Label htmlFor="agent-web">{t("Mag op het web")}</Label>
                <p className="text-xs text-muted-foreground">
                  {t("Deze agent mag dan webpagina's lezen. Hij klikt niets aan en vult niets in, en elke pagina die hij leest staat in zijn spoor.")}
                </p>
              </div>
              <Switch
                id="agent-web"
                checked={form.web_toegang}
                onCheckedChange={(aan) => setForm(prev => ({ ...prev, web_toegang: aan }))}
                data-testid="agent-web"
              />
            </div>

            <Button onClick={save} disabled={!form.name.trim() || saving} data-testid="save-agent-btn">
              {saving && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
              {editingId ? t("Opslaan") : t("Aanmaken")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <MobileBottomNav />
    </div>
  );
}
